import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatDate } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const PenProduction = () => {
  const [entries, setEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dateEntries, setDateEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    pen_number: '1',
    large_crates: '0',
    large_singles: '0',
    medium_crates: '0',
    medium_singles: '0',
    small_crates: '0',
    small_singles: '0',
    cracked_eggs: '0',
    staff_name: '',
    notes: ''
  });

  useEffect(() => {
    fetchEntries();
    fetchDateEntries(selectedDate);
  }, [selectedDate]);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/pen-production`, getAuthHeader());
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to fetch pen production entries');
    }
  };

  const fetchDateEntries = async (date) => {
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/pen-production/date/${date}`, getAuthHeader()),
        axios.get(`${API}/pen-production/summary/${date}`, getAuthHeader())
      ]);
      setDateEntries(entriesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch date entries');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntry) {
        await axios.put(`${API}/pen-production/${editingEntry.id}`, formData, getAuthHeader());
        toast.success('Entry updated successfully');
      } else {
        await axios.post(`${API}/pen-production`, formData, getAuthHeader());
        toast.success('Entry added successfully');
      }
      fetchEntries();
      fetchDateEntries(selectedDate);
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await axios.delete(`${API}/pen-production/${id}`, getAuthHeader());
      toast.success('Entry deleted successfully');
      fetchEntries();
      fetchDateEntries(selectedDate);
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      pen_number: entry.pen_number.toString(),
      large_crates: entry.large_crates?.toString() || '0',
      large_singles: entry.large_singles?.toString() || '0',
      medium_crates: entry.medium_crates?.toString() || '0',
      medium_singles: entry.medium_singles?.toString() || '0',
      small_crates: entry.small_crates?.toString() || '0',
      small_singles: entry.small_singles?.toString() || '0',
      cracked_eggs: entry.cracked_eggs?.toString() || '0',
      staff_name: entry.staff_name || '',
      notes: entry.notes || ''
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingEntry(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      pen_number: '1',
      large_crates: '0',
      large_singles: '0',
      medium_crates: '0',
      medium_singles: '0',
      small_crates: '0',
      small_singles: '0',
      cracked_eggs: '0',
      staff_name: '',
      notes: ''
    });
  };

  const getTotalEggs = (entry) => {
    const largeCrates = parseInt(entry.large_crates || 0);
    const largeSingles = parseInt(entry.large_singles || 0);
    const mediumCrates = parseInt(entry.medium_crates || 0);
    const mediumSingles = parseInt(entry.medium_singles || 0);
    const smallCrates = parseInt(entry.small_crates || 0);
    const smallSingles = parseInt(entry.small_singles || 0);
    const cracked = parseInt(entry.cracked_eggs || 0);
    
    return (largeCrates * 30) + largeSingles + (mediumCrates * 30) + mediumSingles + (smallCrates * 30) + smallSingles + cracked;
  };

  return (
    <div className="space-y-6" data-testid="pen-production-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Daily Production by Pen
          </h1>
          <p className="text-lg text-slate-600 mt-2">Record egg production for each pen (1-5)</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-pen-production-button" className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="pen-production-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEntry ? 'Edit Pen Production' : 'Add Pen Production'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    data-testid="pen-date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="pen_number">Pen Number</Label>
                  <Select onValueChange={(value) => setFormData({ ...formData, pen_number: value })} value={formData.pen_number}>
                    <SelectTrigger data-testid="pen-number-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Pen 1</SelectItem>
                      <SelectItem value="2">Pen 2</SelectItem>
                      <SelectItem value="3">Pen 3</SelectItem>
                      <SelectItem value="4">Pen 4</SelectItem>
                      <SelectItem value="5">Pen 5</SelectItem>
                      <SelectItem value="6">Pen 6</SelectItem>
                      <SelectItem value="7">Brooding Pen</SelectItem>
                      <SelectItem value="8">Grower Pen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm mb-3">Large Size</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="large_crates">Crates (30 eggs)</Label>
                    <Input
                      id="large_crates"
                      type="number"
                      data-testid="pen-large-crates-input"
                      value={formData.large_crates}
                      onChange={(e) => setFormData({ ...formData, large_crates: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="large_singles">Singles</Label>
                    <Input
                      id="large_singles"
                      type="number"
                      data-testid="pen-large-singles-input"
                      value={formData.large_singles}
                      onChange={(e) => setFormData({ ...formData, large_singles: e.target.value })}
                      min="0"
                      max="29"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm mb-3">Medium Size</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="medium_crates">Crates (30 eggs)</Label>
                    <Input
                      id="medium_crates"
                      type="number"
                      data-testid="pen-medium-crates-input"
                      value={formData.medium_crates}
                      onChange={(e) => setFormData({ ...formData, medium_crates: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="medium_singles">Singles</Label>
                    <Input
                      id="medium_singles"
                      type="number"
                      data-testid="pen-medium-singles-input"
                      value={formData.medium_singles}
                      onChange={(e) => setFormData({ ...formData, medium_singles: e.target.value })}
                      min="0"
                      max="29"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm mb-3">Small Size</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="small_crates">Crates (30 eggs)</Label>
                    <Input
                      id="small_crates"
                      type="number"
                      data-testid="pen-small-crates-input"
                      value={formData.small_crates}
                      onChange={(e) => setFormData({ ...formData, small_crates: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="small_singles">Singles</Label>
                    <Input
                      id="small_singles"
                      type="number"
                      data-testid="pen-small-singles-input"
                      value={formData.small_singles}
                      onChange={(e) => setFormData({ ...formData, small_singles: e.target.value })}
                      min="0"
                      max="29"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label htmlFor="cracked_eggs">Cracked Eggs</Label>
                <Input
                  id="cracked_eggs"
                  type="number"
                  data-testid="pen-cracked-input"
                  value={formData.cracked_eggs}
                  onChange={(e) => setFormData({ ...formData, cracked_eggs: e.target.value })}
                  min="0"
                />
              </div>

              <div>
                <Label htmlFor="staff_name">Staff Completing this Entry *</Label>
                <Input
                  id="staff_name"
                  data-testid="pen-staff-name-input"
                  value={formData.staff_name}
                  onChange={(e) => setFormData({ ...formData, staff_name: e.target.value })}
                  placeholder="Enter your name"
                  required
                />
              </div>

              <div>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  data-testid="pen-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" data-testid="pen-submit-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {editingEntry ? 'Update' : 'Add'}
                </Button>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Date Filter */}
      <div className="card p-4">
        <div className="flex items-center gap-4">
          <Label htmlFor="filter_date" className="text-sm font-medium">View Date:</Label>
          <div className="flex items-center gap-2">
            <Input
              id="filter_date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-48"
            />
            <span className="text-sm font-medium text-slate-600 bg-slate-100 px-3 py-2 rounded">
              {formatDate(selectedDate)}
            </span>
          </div>
        </div>
      </div>

      {/* Daily Summary */}
      {summary && summary.pen_count > 0 && (
        <div className="card p-6 bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-lg bg-emerald-100">
              <TrendingUp className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Daily Total - {formatDate(selectedDate)}</p>
              <p className="text-3xl font-bold text-emerald-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {summary.total_eggs} eggs
              </p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div className="p-3 rounded-lg bg-white border border-slate-200">
              <p className="text-xs text-slate-500">Large</p>
              <p className="text-xl font-bold text-slate-900">{summary.total_large}</p>
            </div>
            <div className="p-3 rounded-lg bg-white border border-slate-200">
              <p className="text-xs text-slate-500">Medium</p>
              <p className="text-xl font-bold text-slate-900">{summary.total_medium}</p>
            </div>
            <div className="p-3 rounded-lg bg-white border border-slate-200">
              <p className="text-xs text-slate-500">Small</p>
              <p className="text-xl font-bold text-slate-900">{summary.total_small}</p>
            </div>
            <div className="p-3 rounded-lg bg-white border border-slate-200">
              <p className="text-xs text-slate-500">Cracked</p>
              <p className="text-xl font-bold text-slate-900">{summary.total_cracked}</p>
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-3">
            These totals are automatically added to Daily Inventory
          </p>
        </div>
      )}

      {/* Pen Production Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Pen</th>
                <th>Large (Crates)</th>
                <th>Large (Singles)</th>
                <th>Medium (Crates)</th>
                <th>Medium (Singles)</th>
                <th>Small (Crates)</th>
                <th>Small (Singles)</th>
                <th>Cracked</th>
                <th>Total</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody data-testid="pen-production-table-body">
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDate(entry.date)}</td>
                  <td className="font-semibold">Pen {entry.pen_number}</td>
                  <td>{entry.large_crates || 0}</td>
                  <td>{entry.large_singles || 0}</td>
                  <td>{entry.medium_crates || 0}</td>
                  <td>{entry.medium_singles || 0}</td>
                  <td>{entry.small_crates || 0}</td>
                  <td>{entry.small_singles || 0}</td>
                  <td>{entry.cracked_eggs || 0}</td>
                  <td className="font-semibold">{getTotalEggs(entry)}</td>
                  <td className="max-w-xs truncate">{entry.notes || '-'}</td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        data-testid={`edit-pen-${entry.id}`}
                        onClick={() => handleEdit(entry)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-pen-${entry.id}`}
                        onClick={() => handleDelete(entry.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {entries.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No pen production entries yet. Add your first entry!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PenProduction;
